import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import { formatSubscriptionDateForDisplay } from "./formatters";
import { extendPauseByMonths } from "./pause-dates";

export interface ExtendPauseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allowedMonths: number[];
  /** Current next billing / paused-until date the extension is added on top of. */
  currentNextBillingDate: string | null;
  onConfirmExtendMonths: (months: number) => void;
  confirmPending: boolean;
}

export function ExtendPauseDialog({
  open,
  onOpenChange,
  allowedMonths,
  currentNextBillingDate,
  onConfirmExtendMonths,
  confirmPending,
}: ExtendPauseDialogProps) {
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedMonths(
        allowedMonths.includes(1) ? 1 : (allowedMonths[0] ?? null),
      );
    }
  }, [open, allowedMonths]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Extend pause
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
          <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
            Choose how long to extend your pause. You won&apos;t be charged
            during this time.
          </p>
        </DialogHeader>
        <fieldset className="tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0 tw:mt-2">
          {allowedMonths.map((m) => {
            const inputId = `extend-pause-months-${m}`;
            const selected = selectedMonths === m;
            const resumeDate = currentNextBillingDate
              ? extendPauseByMonths(currentNextBillingDate, m)
              : null;
            return (
              <label
                key={m}
                htmlFor={inputId}
                className={cn(
                  "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-4 tw:text-sm tw:transition-colors",
                  selected
                    ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                    : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
                )}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="extend-pause-months"
                  value={m}
                  checked={selected}
                  disabled={confirmPending}
                  onChange={() => setSelectedMonths(m)}
                  className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
                />
                <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                  <span
                    className="tw:font-medium tw:leading-snug"
                    style={{ color: "var(--so-text, #523D33)" }}
                  >
                    {m} month{m > 1 ? "s" : ""}
                  </span>
                  {resumeDate ? (
                    <span className="tw:text-xs tw:text-[#523D33]/70 tw:leading-snug">
                      Resumes {formatSubscriptionDateForDisplay(resumeDate)}
                    </span>
                  ) : null}
                </div>
              </label>
            );
          })}
        </fieldset>
        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={confirmPending || selectedMonths == null}
            onClick={() => {
              if (selectedMonths != null) onConfirmExtendMonths(selectedMonths);
            }}
            className={PILL_OUTLINE_CLASS}
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Extend pause"
            )}
          </Button>
          <Button
            type="button"
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={PILL_FILLED_CLASS}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
